import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faMinus,
  faTrash,
  faShoppingCart,
  faBagShopping,
  faMotorcycle,
  faSearch,
  faTimes,
  faArrowRight,
  faCheckCircle,
  faReceipt,
  faHome,
  faLocationDot,
  faSpinner,
  faXmark,
  faPrint,
  faUtensils,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import useSWR from "swr";
import ReceiptPrinterEncoder from "@point-of-sale/receipt-printer-encoder";

// ============ CONSTANTS ============
const API_BASE_URL =
  process.env.REACT_APP_BASE_URL ||
  "https://theheadquarters.salcommsystems.click";
const MENU_API_URL =
  process.env.REACT_APP_MENU_ITEMS_API || `${API_BASE_URL}/menu-items-api`;

const ORDER_TYPES = {
  TAKEAWAY: "takeaway",
  DELIVERY: "delivery",
  DINE_IN: "dine-in",
};

// ============ FETCHER ============
const fetcher = async (url) => {
  try {
    const response = await fetch(url, {
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error("Fetcher error:", error);
    return [];
  }
};

// ============ UTILITY FUNCTIONS ============
const formatCurrency = (amount) => {
  return `GH₵${Number(amount || 0).toFixed(2)}`;
};

// Helper to fetch CSRF token
const fetchCsrfToken = async () => {
  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}/session/token`,
    {
      method: "GET",
      credentials: "include",
    },
  );
  if (!response.ok) {
    throw new Error("Failed to fetch CSRF token");
  }
  return await response.text();
};

// ============ CUSTOM HOOK - DEBOUNCE ============
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ============ MAIN COMPONENT ============
const AddOrderModal = ({ isOpen, onClose, onOrderCreated, existingOrder }) => {
  // ============ STATE ============
  const [orderType, setOrderType] = useState(ORDER_TYPES.TAKEAWAY);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [guestName, setGuestName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [tableNumber, setTableNumber] = useState("");
  const [numberOfPeople, setNumberOfPeople] = useState(2);
  const [waiterName, setWaiterName] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [isAdditionalOrder, setIsAdditionalOrder] = useState(false);
  const [previousTotal, setPreviousTotal] = useState(0);
  const [currentOrderId, setCurrentOrderId] = useState(null);

  // Printer states
  const [printing, setPrinting] = useState(false);
  const [printerIP, setPrinterIP] = useState("192.168.1.234");
  const [printerPort, setPrinterPort] = useState(9100);

  const specialRequestsRef = useRef(null);
  const searchInputRef = useRef(null);

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // ============ SWR HOOKS ============
  const {
    data: menuData,
    error: menuError,
    isLoading: menuLoading,
  } = useSWR(MENU_API_URL, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    refreshInterval: 0,
    fallbackData: [],
  });

  // ============ MEMOIZED VALUES ============
  const menu = useMemo(() => {
    if (!menuData || !Array.isArray(menuData)) return [];

    return menuData
      .map((item) => {
        let category = "mains";
        if (item.field_category) {
          category = item.field_category;
        } else if (item.category) {
          category = item.category;
        } else if (item.field_category_target_id) {
          category = item.field_category_target_id;
        } else if (item.field_category_name) {
          category = item.field_category_name;
        } else if (item.menu_category) {
          category = item.menu_category;
        } else if (item.taxonomy_term) {
          category = item.taxonomy_term;
        }

        category = String(category || "mains").trim();
        if (category === "") category = "mains";

        return {
          id: item.uuid || item.id || `item-${Math.random()}`,
          name: item.title || item.name || "Untitled",
          price: Number(item.field_price || item.price || 0),
          category: category,
          description: item.field_description || item.description || "",
          isAvailable:
            item.field_availability === "Yes" || item.isAvailable !== false,
          image: item.field_image || item.image || null,
        };
      })
      .filter((item) => item.name !== "Untitled");
  }, [menuData]);

  const categories = useMemo(() => {
    if (!menu || menu.length === 0) {
      return ["all"];
    }

    const categorySet = new Set();
    menu.forEach((item) => {
      if (item.category && typeof item.category === "string") {
        const cat = item.category.trim();
        if (cat !== "") {
          categorySet.add(cat);
        }
      }
    });

    if (categorySet.size === 0) {
      categorySet.add("mains");
    }

    const uniqueCategories = Array.from(categorySet);
    uniqueCategories.sort();

    return ["all", ...uniqueCategories];
  }, [menu]);

  const filteredMenu = useMemo(() => {
    const searchTerm = debouncedSearchQuery.toLowerCase().trim();

    return menu.filter((item) => {
      const matchesCategory =
        activeCategory === "all" || item.category === activeCategory;

      const itemName = (item.name || "").toLowerCase();
      const itemDescription = (item.description || "").toLowerCase();

      const matchesSearch =
        searchTerm === "" ||
        itemName.includes(searchTerm) ||
        itemDescription.includes(searchTerm);

      return matchesCategory && matchesSearch;
    });
  }, [menu, activeCategory, debouncedSearchQuery]);

  const cartTotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [cart]);

  const cartItemCount = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  // ============ CART FUNCTIONS ============
  const addToCart = useCallback((item) => {
    setCart((prev) => {
      const existing = prev.find((x) => x.id === item.id);
      if (existing) {
        return prev.map((x) =>
          x.id === item.id ? { ...x, quantity: x.quantity + 1 } : x,
        );
      }
      return [{ ...item, quantity: 1, notes: "" }, ...prev];
    });
  }, []);

  const updateQuantity = useCallback((id, delta) => {
    setCart((prev) =>
      prev
        .map((item) => {
          if (item.id === id) {
            const newQty = item.quantity + delta;
            return newQty >= 1 ? { ...item, quantity: newQty } : null;
          }
          return item;
        })
        .filter((item) => item !== null),
    );
  }, []);

  const removeFromCart = useCallback((id) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const updateItemNotes = useCallback((id, notes) => {
    setCart((prev) =>
      prev.map((item) => (item.id === id ? { ...item, notes } : item)),
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    setSpecialRequests("");
  }, []);

  // Load saved printer settings
  useEffect(() => {
    const savedIP = localStorage.getItem("printer_ip");
    const savedPort = localStorage.getItem("printer_port");
    if (savedIP) setPrinterIP(savedIP);
    if (savedPort) setPrinterPort(parseInt(savedPort));
  }, []);

  // ============ PRINTING FUNCTION - MATCHES REACT NATIVE LAYOUT ============
  const generateKitchenTicketNodes = (orderData) => {
    console.log("🖨️ Printing with data:", JSON.stringify(orderData, null, 2));
    const maxChars = 48;

    const orderTypeDisplay =
      orderData.order_type === "dine-in"
        ? "DINE IN"
        : orderData.order_type === "takeaway"
          ? "TAKEAWAY"
          : "DELIVERY";

    const nodes = [];

    // HEADER
    nodes.push(
      text("THE HEADQUARTERS EATERY", { align: "center", bold: true, size: 2 }),
    );

    // Additional items header
    if (orderData.is_additional_order) {
      nodes.push(
        text("*** ADDITIONAL ITEMS ***", { align: "center", bold: true }),
      );
    } else {
      nodes.push(text("KITCHEN ORDER", { align: "center", bold: true }));
    }

    nodes.push(line());

    // ORDER DETAILS
    nodes.push(text(`Order #: ${orderData.order_id}`, { align: "left" }));

    // Previous total for additional orders
    if (
      orderData.is_additional_order &&
      orderData.previous_total !== undefined
    ) {
      nodes.push(
        text(`Previous Total: GH₵${orderData.previous_total.toFixed(2)}`, {
          align: "left",
        }),
      );
    }

    nodes.push(text(`Type: ${orderTypeDisplay}`, { align: "left" }));

    // TABLE NUMBER - for dine-in
    if (orderData.order_type === "dine-in" && orderData.table_number) {
      nodes.push(text(`Table: ${orderData.table_number}`, { align: "left" }));

      // SESSION NAME
      if (orderData.session_name) {
        nodes.push(
          text(`Session: ${orderData.session_name}`, { align: "left" }),
        );
      }

      // NUMBER OF GUESTS
      if (orderData.number_of_people) {
        nodes.push(
          text(`Guests: ${orderData.number_of_people}`, { align: "left" }),
        );
      }
    }

    nodes.push(
      text(`Time: ${new Date().toLocaleTimeString()}`, { align: "left" }),
    );
    nodes.push(
      text(`Date: ${new Date().toLocaleDateString()}`, { align: "left" }),
    );

    // Delivery/Takeaway address
    if (
      (orderData.order_type === "delivery" ||
        orderData.order_type === "takeaway") &&
      orderData.delivery_address
    ) {
      const shortAddress =
        orderData.delivery_address.length > 30
          ? orderData.delivery_address.substring(0, 27) + "..."
          : orderData.delivery_address;

      nodes.push(
        text(
          `${orderData.order_type === "delivery" ? "Delivery" : "Pickup"}: ${shortAddress}`,
          { align: "left" },
        ),
      );
    }

    nodes.push(text("", { align: "left" }));
    nodes.push(line());

    // NEW ITEMS section
    nodes.push(text("NEW ITEMS", { align: "left", bold: true }));
    nodes.push(line());

    // ITEMS LIST
    for (const item of orderData.items) {
      const qty = String(item.quantity).padStart(3, " ");
      const name =
        item.name.length > maxChars - 5
          ? item.name.substring(0, maxChars - 5) + ".."
          : item.name;

      nodes.push(text(`${qty}x ${name}`, { align: "left" }));

      if (item.notes && item.notes.trim()) {
        const notes =
          item.notes.length > maxChars - 8
            ? item.notes.substring(0, maxChars - 8) + ".."
            : item.notes;
        nodes.push(text(`   Note: ${notes}`, { align: "left" }));
      }
    }

    // SPECIAL REQUESTS
    if (orderData.special_requests && orderData.special_requests.trim()) {
      nodes.push(text("", { align: "left" }));
      nodes.push(line());
      nodes.push(text("SPECIAL REQUESTS", { align: "left", bold: true }));
      nodes.push(
        text(orderData.special_requests.substring(0, maxChars), {
          align: "left",
        }),
      );
    }

    nodes.push(text("", { align: "left" }));
    nodes.push(line({ character: "=" }));

    // WAITER NAME
    nodes.push(
      text(`Waiter: ${orderData.waiter_name || "N/A"}`, {
        align: "center",
        bold: true,
      }),
    );

    nodes.push(feed(6));
    nodes.push(cut());

    return nodes;
  };

  // Helper functions for the printer
  const text = (content, options = {}) => {
    return { type: "text", content, ...options };
  };

  const line = (options = {}) => {
    const character = options.character || "-";
    return { type: "line", character, ...options };
  };

  const feed = (lines) => {
    return { type: "feed", lines };
  };

  const cut = () => {
    return { type: "cut" };
  };

  // Convert nodes to ESC/POS bytes
  const generateKitchenTicketBytes = (orderData) => {
    const nodes = generateKitchenTicketNodes(orderData);

    const strToBytes = (str) => {
      return [...new TextEncoder().encode(str)];
    };

    const ESC = 0x1b;
    const GS = 0x1d;
    const LF = 0x0a;

    const init = [ESC, 0x40];
    const center = [ESC, 0x61, 0x01];
    const left = [ESC, 0x61, 0x00];
    const boldOn = [ESC, 0x45, 0x01];
    const boldOff = [ESC, 0x45, 0x00];
    const normalSize = [GS, 0x21, 0x00];
    const mediumSize = [GS, 0x21, 0x11];
    const doubleHeight = [GS, 0x21, 0x10];

    const feedBytes = [LF];
    const feed6 = [LF, LF, LF, LF, LF, LF];
    const cutBytes = [GS, 0x56, 0x00];
    const separator = "------------------------------------------------";

    const bytes = [];

    // Initialize printer
    bytes.push(...init);
    bytes.push(...center);
    bytes.push(...boldOn);
    bytes.push(...mediumSize);
    bytes.push(...strToBytes("THE HEADQUARTERS EATERY\n"));
    bytes.push(...normalSize);
    bytes.push(...boldOff);
    bytes.push(...center);
    bytes.push(...boldOn);
    bytes.push(...normalSize);

    // Additional items header
    if (orderData.is_additional_order) {
      bytes.push(...strToBytes("*** ADDITIONAL ITEMS ***\n"));
    } else {
      bytes.push(...strToBytes("--- KITCHEN ORDER ---\n"));
    }

    bytes.push(...boldOff);
    bytes.push(...feedBytes);
    bytes.push(...left);
    bytes.push(...boldOn);
    bytes.push(...strToBytes("Order Details\n"));
    bytes.push(...boldOff);
    bytes.push(...strToBytes(`${separator}\n`));
    bytes.push(...strToBytes(`Order #: ${orderData.order_id}\n`));

    // Previous total for additional orders
    if (
      orderData.is_additional_order &&
      orderData.previous_total !== undefined
    ) {
      bytes.push(
        ...strToBytes(
          `Previous Total: GH₵${orderData.previous_total.toFixed(2)}\n`,
        ),
      );
    }

    bytes.push(...strToBytes(`Type: ${orderData.order_type.toUpperCase()}\n`));

    // TABLE NUMBER - for dine-in
    if (orderData.order_type === "dine-in" && orderData.table_number) {
      bytes.push(...strToBytes(`Table: ${orderData.table_number}\n`));
      if (orderData.session_name) {
        bytes.push(...strToBytes(`Session: ${orderData.session_name}\n`));
      }
      if (orderData.number_of_people) {
        bytes.push(...strToBytes(`Guests: ${orderData.number_of_people}\n`));
      }
    }

    bytes.push(...strToBytes(`Time: ${new Date().toLocaleTimeString()}\n`));
    bytes.push(...strToBytes(`Date: ${new Date().toLocaleDateString()}\n`));

    if (
      (orderData.order_type === "delivery" ||
        orderData.order_type === "takeaway") &&
      orderData.delivery_address
    ) {
      bytes.push(
        ...strToBytes(
          `${orderData.order_type === "delivery" ? "Delivery" : "Pickup"}:\n`,
        ),
      );
      const addressLines =
        orderData.delivery_address.length > 40
          ? orderData.delivery_address.match(/.{1,40}/g) || [
              orderData.delivery_address,
            ]
          : [orderData.delivery_address];
      for (const line of addressLines) {
        bytes.push(...strToBytes(`  ${line}\n`));
      }
    }

    bytes.push(...feedBytes);
    bytes.push(...boldOn);
    bytes.push(...strToBytes("NEW ITEMS\n"));
    bytes.push(...boldOff);
    bytes.push(...strToBytes(`${separator}\n`));
    bytes.push(...strToBytes("QTY  ITEM\n"));
    bytes.push(...strToBytes(`${separator}\n`));

    // ITEMS LIST
    for (const item of orderData.items) {
      const qty = String(item.quantity).padStart(3, " ");
      const name =
        item.name.length > 42 ? item.name.substring(0, 39) + "..." : item.name;

      const itemLine = `${qty}x ${name}\n`;
      bytes.push(...strToBytes(itemLine));

      if (item.notes && item.notes.trim()) {
        const notes =
          item.notes.length > 44
            ? item.notes.substring(0, 41) + "..."
            : item.notes;
        bytes.push(...strToBytes(`   ${notes}\n`));
      }
    }

    bytes.push(...feedBytes);

    // SPECIAL REQUESTS
    if (orderData.special_requests && orderData.special_requests.trim()) {
      bytes.push(...boldOn);
      bytes.push(...strToBytes("Special Requests\n"));
      bytes.push(...boldOff);
      bytes.push(...strToBytes(`${separator}\n`));
      const specialLines =
        orderData.special_requests.length > 45
          ? orderData.special_requests.match(/.{1,45}/g) || [
              orderData.special_requests,
            ]
          : [orderData.special_requests];
      for (const line of specialLines) {
        bytes.push(...strToBytes(`  ${line}\n`));
      }
    }

    bytes.push(...strToBytes(`${separator}\n\n`));
    bytes.push(...strToBytes(`Waiter: ${orderData.waiter_name || "N/A"}\n`));
    bytes.push(...feed6);
    bytes.push(...cutBytes);

    return new Uint8Array(bytes);
  };

  const printKitchenOrder = async (orderData) => {
    if (printing) return;
    setPrinting(true);
    setErrorMessage("");

    try {
      localStorage.setItem("printer_ip", printerIP);
      localStorage.setItem("printer_port", printerPort.toString());

      // Generate the bytes using the same format as React Native
      const dataBytes = generateKitchenTicketBytes(orderData);
      const dataArray = Array.from(dataBytes);

      const response = await fetch("http://localhost:3001/print", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ip: printerIP,
          port: printerPort,
          data: dataArray,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to print kitchen order");
      }

      setSuccessMessage("✅ Kitchen order printed successfully!");
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error("Print failed:", error);
      setErrorMessage(`Print failed: ${error.message}`);
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setPrinting(false);
    }
  };

  // ============ ORDER SUBMISSION ============
  const submitOrder = useCallback(async () => {
    if (cart.length === 0) {
      setErrorMessage("Please add items to your order");
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    if (orderType === ORDER_TYPES.DELIVERY && !deliveryAddress) {
      setErrorMessage("Please enter a delivery address");
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    if (orderType === ORDER_TYPES.TAKEAWAY && !deliveryAddress) {
      setErrorMessage("Please enter a pickup location");
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    if (orderType === ORDER_TYPES.DINE_IN && !tableNumber) {
      setErrorMessage("Please enter a table number");
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    setSubmitting(true);

    const items = cart.map((item) => ({
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: item.quantity,
      notes: item.notes || "",
    }));

    const orderData = {
      order_type: orderType,
      items: items,
      special_requests: specialRequests,
      total_amount: cartTotal,
      guest_name: guestName || "Guest",
      delivery_address:
        orderType !== ORDER_TYPES.DINE_IN ? deliveryAddress : null,
      table_number: orderType === ORDER_TYPES.DINE_IN ? tableNumber : null,
      number_of_people:
        orderType === ORDER_TYPES.DINE_IN ? numberOfPeople : null,
      waiter_name: waiterName || "Staff",
      session_name: orderType === ORDER_TYPES.DINE_IN ? sessionName : null,
    };

    try {
      const csrfToken = await fetchCsrfToken();

      const orderUrl = `${API_BASE_URL}/api/order/create`;
      console.log("Submitting order to:", orderUrl);
      console.log("Order data:", orderData);

      const response = await fetch(orderUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(orderData),
      });

      const contentType = response.headers.get("content-type");
      let result;

      if (contentType && contentType.includes("application/json")) {
        result = await response.json();
      } else {
        const text = await response.text();
        console.error("Non-JSON response:", text.substring(0, 200));
        throw new Error(
          `Server returned ${response.status}: ${text.substring(0, 100)}`,
        );
      }

      if (response.ok) {
        const orderId = result.order_id || result.id || "N/A";

        // Prepare order data for printing with same format as React Native
        const orderForPrint = {
          order_id: orderId,
          order_type: orderType,
          table_number: orderType === ORDER_TYPES.DINE_IN ? tableNumber : null,
          number_of_people:
            orderType === ORDER_TYPES.DINE_IN ? numberOfPeople : null,
          delivery_address:
            orderType !== ORDER_TYPES.DINE_IN ? deliveryAddress : null,
          special_requests: specialRequests,
          items: items,
          waiter_name: waiterName || "Staff",
          session_name: orderType === ORDER_TYPES.DINE_IN ? sessionName : null,
          is_additional_order: isAdditionalOrder,
          previous_total: previousTotal || 0,
        };

        console.log("📋 Print Data:", JSON.stringify(orderForPrint, null, 2));

        setSuccessMessage(`Order #${orderId} created successfully!`);
        setShowSuccess(true);

        // Clear cart immediately
        setCart([]);
        setGuestName("");
        setDeliveryAddress("");
        setSpecialRequests("");
        setSearchQuery("");

        if (onOrderCreated) {
          onOrderCreated(result);
        }

        // Print kitchen order IMMEDIATELY after order creation
        await printKitchenOrder(orderForPrint);

        // Close modal after a short delay
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setErrorMessage(
          result.error || result.message || "Failed to create order",
        );
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    } catch (error) {
      console.error("Order submission error:", error);
      setErrorMessage(error.message || "Network error. Please try again.");
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setSubmitting(false);
    }
  }, [
    cart,
    orderType,
    deliveryAddress,
    specialRequests,
    guestName,
    cartTotal,
    tableNumber,
    numberOfPeople,
    waiterName,
    sessionName,
    isAdditionalOrder,
    previousTotal,
    onOrderCreated,
    onClose,
  ]);

  // ============ EFFECTS ============
  useEffect(() => {
    if (existingOrder) {
      setCart(existingOrder.items || []);
      setOrderType(existingOrder.order_type || ORDER_TYPES.TAKEAWAY);
      setGuestName(existingOrder.guest_name || "");
      setDeliveryAddress(existingOrder.delivery_address || "");
      setSpecialRequests(existingOrder.special_requests || "");
      setTableNumber(existingOrder.table_number || "");
      setNumberOfPeople(existingOrder.number_of_people || 2);
      setWaiterName(existingOrder.waiter_name || "");
      setSessionName(existingOrder.session_name || "");
    }
  }, [existingOrder]);

  useEffect(() => {
    if (menuError) {
      setErrorMessage("Failed to load menu: " + menuError.message);
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
  }, [menuError]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // ============ RENDER ============
  if (!isOpen) return null;

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.backdrop} onClick={onClose}></div>
      <div style={modalStyles.content}>
        {/* HEADER */}
        <div style={modalStyles.header}>
          <div>
            <h2 style={modalStyles.title}>
              {existingOrder ? "Edit Order" : "Create New Order"}
            </h2>
          </div>
          <button style={modalStyles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} size="2x" />
          </button>
        </div>

        {/* NOTIFICATIONS */}
        {showError && (
          <div style={modalStyles.errorNotification}>
            <FontAwesomeIcon icon={faTimes} style={{ marginRight: "8px" }} />
            {errorMessage}
            <button
              style={modalStyles.notificationClose}
              onClick={() => setShowError(false)}
            >
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>
          </div>
        )}

        {showSuccess && (
          <div style={modalStyles.successNotification}>
            <FontAwesomeIcon
              icon={faCheckCircle}
              style={{ marginRight: "8px" }}
            />
            {successMessage}
            <button
              style={modalStyles.notificationClose}
              onClick={() => setShowSuccess(false)}
            >
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>
          </div>
        )}

        <div style={modalStyles.body}>
          {/* ORDER TYPE SELECTOR */}
          <div style={modalStyles.orderTypeContainer}>
            <button
              style={{
                ...modalStyles.orderTypeButton,
                ...(orderType === ORDER_TYPES.DINE_IN
                  ? modalStyles.orderTypeButtonActive
                  : {}),
              }}
              onClick={() => setOrderType(ORDER_TYPES.DINE_IN)}
            >
              <FontAwesomeIcon
                icon={faUtensils}
                size="lg"
                style={{
                  color:
                    orderType === ORDER_TYPES.DINE_IN ? "#FF6B35" : "#6B7280",
                  marginRight: "8px",
                }}
              />
              <span
                style={{
                  color:
                    orderType === ORDER_TYPES.DINE_IN ? "#FF6B35" : "#6B7280",
                  fontWeight: orderType === ORDER_TYPES.DINE_IN ? "600" : "500",
                  fontSize: "18px",
                }}
              >
                Dine In
              </span>
            </button>
            <button
              style={{
                ...modalStyles.orderTypeButton,
                ...(orderType === ORDER_TYPES.TAKEAWAY
                  ? modalStyles.orderTypeButtonActive
                  : {}),
              }}
              onClick={() => setOrderType(ORDER_TYPES.TAKEAWAY)}
            >
              <FontAwesomeIcon
                icon={faBagShopping}
                size="lg"
                style={{
                  color:
                    orderType === ORDER_TYPES.TAKEAWAY ? "#FF6B35" : "#6B7280",
                  marginRight: "8px",
                }}
              />
              <span
                style={{
                  color:
                    orderType === ORDER_TYPES.TAKEAWAY ? "#FF6B35" : "#6B7280",
                  fontWeight:
                    orderType === ORDER_TYPES.TAKEAWAY ? "600" : "500",
                  fontSize: "18px",
                }}
              >
                Takeaway
              </span>
            </button>
            <button
              style={{
                ...modalStyles.orderTypeButton,
                ...(orderType === ORDER_TYPES.DELIVERY
                  ? modalStyles.orderTypeButtonActive
                  : {}),
              }}
              onClick={() => setOrderType(ORDER_TYPES.DELIVERY)}
            >
              <FontAwesomeIcon
                icon={faMotorcycle}
                size="lg"
                style={{
                  color:
                    orderType === ORDER_TYPES.DELIVERY ? "#FF6B35" : "#6B7280",
                  marginRight: "8px",
                }}
              />
              <span
                style={{
                  color:
                    orderType === ORDER_TYPES.DELIVERY ? "#FF6B35" : "#6B7280",
                  fontWeight:
                    orderType === ORDER_TYPES.DELIVERY ? "600" : "500",
                  fontSize: "18px",
                }}
              >
                Delivery
              </span>
            </button>
          </div>

          {/* DINE-IN DETAILS */}
          {orderType === ORDER_TYPES.DINE_IN && (
            <div style={modalStyles.dineInContainer}>
              <div style={modalStyles.dineInRow}>
                <div style={modalStyles.dineInField}>
                  <label style={modalStyles.fieldLabel}>Table Number *</label>
                  <input
                    type="text"
                    placeholder="e.g., 5"
                    value={tableNumber}
                    onChange={(e) => setTableNumber(e.target.value)}
                    style={modalStyles.fieldInput}
                  />
                </div>
                <div style={modalStyles.dineInField}>
                  <label style={modalStyles.fieldLabel}>Number of Guests</label>
                  <input
                    type="number"
                    placeholder="2"
                    value={numberOfPeople}
                    onChange={(e) =>
                      setNumberOfPeople(parseInt(e.target.value) || 1)
                    }
                    style={modalStyles.fieldInput}
                    min="1"
                  />
                </div>
              </div>
              <div style={modalStyles.dineInRow}>
                <div style={modalStyles.dineInField}>
                  <label style={modalStyles.fieldLabel}>
                    Session Name (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Lunch Service"
                    value={sessionName}
                    onChange={(e) => setSessionName(e.target.value)}
                    style={modalStyles.fieldInput}
                  />
                </div>
                <div style={modalStyles.dineInField}>
                  <label style={modalStyles.fieldLabel}>Waiter Name</label>
                  <input
                    type="text"
                    placeholder="Staff name"
                    value={waiterName}
                    onChange={(e) => setWaiterName(e.target.value)}
                    style={modalStyles.fieldInput}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ADDRESS INPUT for Takeaway/Delivery */}
          {(orderType === ORDER_TYPES.TAKEAWAY ||
            orderType === ORDER_TYPES.DELIVERY) && (
            <div style={modalStyles.addressSearchContainer}>
              <div style={modalStyles.addressInputWrapper}>
                <div style={modalStyles.addressInput}>
                  <FontAwesomeIcon
                    icon={
                      orderType === ORDER_TYPES.TAKEAWAY
                        ? faHome
                        : faLocationDot
                    }
                    color="#9CA3AF"
                    size="lg"
                  />
                  <input
                    type="text"
                    placeholder={
                      orderType === ORDER_TYPES.TAKEAWAY
                        ? "Pickup Location (e.g., Store Name) *"
                        : "Delivery Address *"
                    }
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    style={modalStyles.addressInputField}
                  />
                </div>
              </div>
            </div>
          )}

          {/* SEARCH */}
          <div style={modalStyles.searchWrapper}>
            <div style={modalStyles.searchInput}>
              <FontAwesomeIcon icon={faSearch} color="#9CA3AF" size="lg" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search menu items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={modalStyles.searchField}
                autoComplete="off"
                spellCheck="false"
              />
            </div>
          </div>

          {/* CATEGORIES */}
          <div style={modalStyles.categoriesWrapper}>
            <div style={modalStyles.categoriesSection}>
              {categories && categories.length > 0 ? (
                categories.map((category) => (
                  <button
                    key={category}
                    style={{
                      ...modalStyles.categoryChip,
                      ...(activeCategory === category
                        ? modalStyles.categoryChipActive
                        : {}),
                    }}
                    onClick={() => setActiveCategory(category)}
                  >
                    {category === "all"
                      ? "All Items"
                      : category.charAt(0).toUpperCase() + category.slice(1)}
                  </button>
                ))
              ) : (
                <>
                  <button
                    style={{
                      ...modalStyles.categoryChip,
                      ...(activeCategory === "all"
                        ? modalStyles.categoryChipActive
                        : {}),
                    }}
                    onClick={() => setActiveCategory("all")}
                  >
                    All Items
                  </button>
                  <button
                    style={{
                      ...modalStyles.categoryChip,
                      ...(activeCategory === "mains"
                        ? modalStyles.categoryChipActive
                        : {}),
                    }}
                    onClick={() => setActiveCategory("mains")}
                  >
                    Mains
                  </button>
                  <button
                    style={{
                      ...modalStyles.categoryChip,
                      ...(activeCategory === "sides"
                        ? modalStyles.categoryChipActive
                        : {}),
                    }}
                    onClick={() => setActiveCategory("sides")}
                  >
                    Sides
                  </button>
                  <button
                    style={{
                      ...modalStyles.categoryChip,
                      ...(activeCategory === "drinks"
                        ? modalStyles.categoryChipActive
                        : {}),
                    }}
                    onClick={() => setActiveCategory("drinks")}
                  >
                    Drinks
                  </button>
                </>
              )}
            </div>
          </div>

          {/* MENU & CART */}
          <div style={modalStyles.mainGrid}>
            {/* Menu Section */}
            <div
              style={modalStyles.menuSection}
              key={debouncedSearchQuery + activeCategory}
            >
              {menuLoading ? (
                <div style={modalStyles.loadingContainer}>
                  <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    size="3x"
                    color="#FF6B35"
                  />
                  <p
                    style={{
                      marginTop: "12px",
                      color: "#6B7280",
                      fontSize: "16px",
                    }}
                  >
                    Loading menu...
                  </p>
                </div>
              ) : filteredMenu.length === 0 ? (
                <div style={modalStyles.emptyContainer}>
                  <FontAwesomeIcon icon={faSearch} size="4x" color="#DDD" />
                  <p
                    style={{
                      marginTop: "12px",
                      color: "#9CA3AF",
                      fontSize: "16px",
                    }}
                  >
                    {searchQuery
                      ? "No items match your search"
                      : "No items found"}
                  </p>
                  {searchQuery && (
                    <button
                      style={{
                        marginTop: "8px",
                        padding: "8px 16px",
                        backgroundColor: "#FF6B35",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                      onClick={() => setSearchQuery("")}
                    >
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div
                    style={{
                      marginBottom: "8px",
                      color: "#6B7280",
                      fontSize: "14px",
                    }}
                  >
                    Showing {filteredMenu.length} item
                    {filteredMenu.length !== 1 ? "s" : ""}
                  </div>
                  {filteredMenu.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: "flex",
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                        padding: "20px",
                        marginBottom: "12px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                        cursor: item.isAvailable ? "pointer" : "not-allowed",
                        opacity: item.isAvailable ? 1 : 0.6,
                        border: "1px solid #f0f0f0",
                        minHeight: "80px",
                      }}
                      onClick={() => item.isAvailable && addToCart(item)}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <h4
                            style={{
                              margin: 0,
                              fontSize: "18px",
                              fontWeight: "600",
                              color: "#1F2937",
                            }}
                          >
                            {item.name}
                          </h4>
                          {!item.isAvailable && (
                            <span
                              style={{
                                backgroundColor: "#FEE2E2",
                                color: "#DC2626",
                                padding: "4px 12px",
                                borderRadius: "4px",
                                fontSize: "13px",
                                fontWeight: "600",
                              }}
                            >
                              Unavailable
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <p
                            style={{
                              margin: "4px 0 8px",
                              fontSize: "15px",
                              color: "#6B7280",
                            }}
                          >
                            {item.description}
                          </p>
                        )}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "20px",
                              fontWeight: "700",
                              color: "#FF6B35",
                            }}
                          >
                            {formatCurrency(item.price)}
                          </span>
                          {item.isAvailable && (
                            <button
                              style={{
                                border: "none",
                                borderRadius: "50%",
                                width: "48px",
                                height: "48px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "#000",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                addToCart(item);
                              }}
                            >
                              <FontAwesomeIcon icon={faPlus} size="lg" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Cart Section */}
            <div style={modalStyles.cartSection}>
              <div style={modalStyles.cartHeader}>
                <span
                  style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#1F2937",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faShoppingCart}
                    style={{ marginRight: "8px", color: "#FF6B35" }}
                  />
                  Current Order
                </span>
                <span style={{ fontSize: "16px", color: "#6B7280" }}>
                  {cartItemCount} item{cartItemCount !== 1 ? "s" : ""}
                </span>
              </div>

              <div style={modalStyles.cartList}>
                {cart.length === 0 ? (
                  <div style={modalStyles.emptyCart}>
                    <FontAwesomeIcon
                      icon={faShoppingCart}
                      size="4x"
                      color="#DDD"
                    />
                    <p
                      style={{
                        marginTop: "12px",
                        color: "#9CA3AF",
                        fontSize: "16px",
                      }}
                    >
                      Your order is empty
                    </p>
                    <p style={{ fontSize: "15px", color: "#D1D5DB" }}>
                      Tap menu items to add
                    </p>
                  </div>
                ) : (
                  cart.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        padding: "16px 0",
                        borderBottom: "1px solid #F0F0F0",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "#1F2937",
                              }}
                            >
                              {item.name}
                            </span>
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "#FF6B35",
                              }}
                            >
                              {formatCurrency(item.price * item.quantity)}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "54px",
                              marginTop: "10px",
                            }}
                          >
                            <button
                              style={{
                                backgroundColor: "#1F2937",
                                border: "none",
                                borderRadius: "50%",
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "#fff",
                              }}
                              onClick={() => updateQuantity(item.id, -1)}
                            >
                              <FontAwesomeIcon icon={faMinus} size="lg" />
                            </button>
                            <span
                              style={{
                                fontSize: "18px",
                                fontWeight: "600",
                                minWidth: "32px",
                                textAlign: "center",
                              }}
                            >
                              {item.quantity}
                            </span>
                            <button
                              style={{
                                backgroundColor: "#1F2937",
                                border: "none",
                                borderRadius: "50%",
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "#fff",
                              }}
                              onClick={() => updateQuantity(item.id, 1)}
                            >
                              <FontAwesomeIcon icon={faPlus} size="lg" />
                            </button>
                            <button
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: "#EF4444",
                                marginLeft: "auto",
                                borderRadius: "50%",
                                width: "46px",
                                height: "46px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              onClick={() => removeFromCart(item.id)}
                            >
                              <FontAwesomeIcon icon={faTrash} size="lg" />
                            </button>
                          </div>
                          <input
                            type="text"
                            placeholder="Add notes (e.g., no onions)"
                            value={item.notes || ""}
                            onChange={(e) =>
                              updateItemNotes(item.id, e.target.value)
                            }
                            style={{
                              width: "100%",
                              marginTop: "10px",
                              padding: "10px 14px",
                              border: "1px solid #E5E7EB",
                              borderRadius: "8px",
                              fontSize: "15px",
                              color: "#1F2937",
                              backgroundColor: "#F9FAFB",
                              minHeight: "44px",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Special Requests */}
              <div style={modalStyles.specialRequests}>
                <textarea
                  ref={specialRequestsRef}
                  placeholder="Special requests or notes for the entire order..."
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  rows={2}
                  style={modalStyles.specialRequestsField}
                />
              </div>

              {/* Cart Footer */}
              <div style={modalStyles.cartFooter}>
                <div style={modalStyles.totalSection}>
                  <span
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#1F2937",
                    }}
                  >
                    Total:
                  </span>
                  <span
                    style={{
                      fontSize: "24px",
                      fontWeight: "700",
                      color: "#FF6B35",
                    }}
                  >
                    {formatCurrency(cartTotal)}
                  </span>
                </div>
                <div style={modalStyles.actionButtons}>
                  <button
                    style={{
                      ...modalStyles.clearButton,
                      ...(cart.length === 0
                        ? modalStyles.clearButtonDisabled
                        : {}),
                    }}
                    onClick={clearCart}
                    disabled={cart.length === 0}
                  >
                    <FontAwesomeIcon icon={faTrash} size="lg" />
                    Clear
                  </button>
                  <button
                    style={{
                      ...modalStyles.submitButton,
                      ...(cart.length === 0 || submitting
                        ? modalStyles.submitButtonDisabled
                        : {}),
                    }}
                    onClick={submitOrder}
                    disabled={cart.length === 0 || submitting}
                  >
                    {submitting ? (
                      <FontAwesomeIcon icon={faSpinner} spin size="lg" />
                    ) : (
                      <>
                        Place Order
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          style={{ marginLeft: "8px" }}
                          size="lg"
                        />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ STYLES ============
const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  content: {
    position: "relative",
    backgroundColor: "#fff",
    borderRadius: "20px",
    width: "95%",
    maxWidth: "1400px",
    maxHeight: "94vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    overflow: "hidden",
  },
  header: {
    padding: "4px 32px",
    borderBottom: "1px solid #E2E8F0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexShrink: 0,
  },
  title: {
    fontSize: "20px",
    fontWeight: "700",
    margin: 0,
    color: "#1F2937",
  },
  closeButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#9CA3AF",
    padding: "12px",
    borderRadius: "8px",
    transition: "background 0.2s",
    minWidth: "48px",
    minHeight: "48px",
  },
  body: {
    padding: "24px 32px",
    overflowY: "auto",
    flex: 1,
  },
  orderTypeContainer: {
    display: "flex",
    gap: "12px",
    marginBottom: "16px",
  },
  orderTypeButton: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    borderRadius: "10px",
    border: "2px solid #E5E7EB",
    backgroundColor: "#F9FAFB",
    cursor: "pointer",
    transition: "all 0.2s",
    minHeight: "64px",
  },
  orderTypeButtonActive: {
    border: "2px solid #FF6B35",
    backgroundColor: "#FEF3F2",
  },
  dineInContainer: {
    marginBottom: "16px",
    backgroundColor: "#F8FAFC",
    padding: "16px",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  dineInRow: {
    display: "flex",
    gap: "12px",
    marginBottom: "12px",
  },
  dineInRow: {
    display: "flex",
    gap: "12px",
  },
  dineInField: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  fieldLabel: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151",
  },
  fieldInput: {
    padding: "10px 14px",
    border: "1px solid #E5E7EB",
    borderRadius: "8px",
    fontSize: "15px",
    color: "#1F2937",
    backgroundColor: "#FFFFFF",
    minHeight: "44px",
    width: "100%",
  },
  addressSearchContainer: {
    display: "flex",
    gap: "12px",
    marginBottom: "16px",
    alignItems: "center",
  },
  addressInputWrapper: {
    flex: 1,
    minWidth: "0",
  },
  addressInput: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px 18px",
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  addressInputField: {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: "16px",
    color: "#1F2937",
    backgroundColor: "transparent",
    minHeight: "44px",
  },
  searchWrapper: {
    marginBottom: "16px",
  },
  searchInput: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px 18px",
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  searchField: {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: "16px",
    color: "#1F2937",
    backgroundColor: "transparent",
    minHeight: "44px",
  },
  categoriesWrapper: {
    marginBottom: "16px",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  categoriesSection: {
    display: "flex",
    gap: "28px",
    paddingBottom: "4px",
    whiteSpace: "nowrap",
  },
  categoryChip: {
    padding: "10px 20px",
    borderRadius: "20px",
    border: "1px solid #E5E7EB",
    backgroundColor: "#F9FAFB",
    fontSize: "16px",
    color: "#6B7280",
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "all 0.2s",
    flexShrink: 0,
    minHeight: "48px",
  },
  categoryChipActive: {
    backgroundColor: "#FF6B35",
    border: "1px solid #FF6B35",
    color: "#FFFFFF",
  },
  mainGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    minHeight: "400px",
  },
  menuSection: {
    height: "500px",
    overflowY: "auto",
    paddingRight: "8px",
  },
  cartSection: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    overflow: "hidden",
    height: "500px",
  },
  cartHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    backgroundColor: "#F9FAFB",
    borderBottom: "1px solid #E5E7EB",
    flexShrink: 0,
    minHeight: "60px",
  },
  cartList: {
    flex: 1,
    overflowY: "auto",
    padding: "0 20px",
  },
  specialRequests: {
    padding: "12px 20px",
    borderTop: "1px solid #E5E7EB",
    flexShrink: 0,
  },
  specialRequestsField: {
    width: "100%",
    padding: "12px 16px",
    border: "1px solid #E5E7EB",
    borderRadius: "8px",
    fontSize: "15px",
    color: "#1F2937",
    resize: "vertical",
    minHeight: "60px",
    fontFamily: "inherit",
  },
  cartFooter: {
    padding: "16px 20px",
    borderTop: "1px solid #E5E7EB",
    backgroundColor: "#F9FAFB",
    flexShrink: 0,
  },
  totalSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  actionButtons: {
    display: "flex",
    gap: "12px",
  },
  clearButton: {
    flex: 1,
    padding: "14px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#FEF2F2",
    color: "#EF4444",
    fontWeight: "600",
    fontSize: "16px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    transition: "all 0.2s",
    minHeight: "56px",
  },
  clearButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  submitButton: {
    flex: 2,
    padding: "14px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#4f9c49",
    color: "#fff",
    fontWeight: "600",
    fontSize: "18px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    transition: "all 0.2s",
    minHeight: "56px",
  },
  submitButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    minHeight: "200px",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    minHeight: "200px",
  },
  emptyCart: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    minHeight: "150px",
  },
  errorNotification: {
    display: "flex",
    alignItems: "center",
    padding: "16px 24px",
    margin: "0 32px 16px 32px",
    backgroundColor: "#FEE2E2",
    border: "1px solid #FECACA",
    borderRadius: "8px",
    color: "#DC2626",
    fontSize: "16px",
  },
  successNotification: {
    display: "flex",
    alignItems: "center",
    padding: "16px 24px",
    margin: "0 32px 16px 32px",
    backgroundColor: "#D1FAE5",
    border: "1px solid #A7F3D0",
    borderRadius: "8px",
    color: "#059669",
    fontSize: "16px",
  },
  notificationClose: {
    marginLeft: "auto",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "inherit",
    padding: "8px",
    minWidth: "40px",
    minHeight: "40px",
  },
};

export default AddOrderModal;
